import React, { useState } from 'react';
import { useChildProgress } from '../hooks/useDashboard';
import { DailyActivities } from './DailyActivities';
import { ActivityScoreView } from './ActivityScoreView';

const TAB_BY_DATE = 'by_date';
const TAB_BY_ACTIVITY = 'by_activity';

export const ChildProgress = ({ child, onClose, t, formatNumber, formatDate }) => {
  const { data, isLoading, error } = useChildProgress(child?.id, !!child);
  const [selectedDate, setSelectedDate] = useState(null);
  const [activeTab, setActiveTab] = useState(TAB_BY_DATE);

  if (!child) return null;

  const renderProgressBar = (achieved, target) => {
    const percentage = target > 0 ? Math.min((achieved / target) * 100, 100) : 0;
    const isComplete = achieved >= target && target > 0;

    return (
      <div className="d-flex align-items-center gap-2" style={{ minWidth: '120px' }}>
        <div
          className="bg-secondary bg-opacity-25 rounded-pill flex-grow-1"
          style={{ height: '8px', position: 'relative' }}
        >
          <div
            className={`rounded-pill ${isComplete ? 'bg-success' : 'bg-primary'}`}
            style={{
              width: `${percentage}%`,
              height: '8px',
              position: 'absolute',
              top: '0',
              left: '0',
              transition: 'width 0.3s ease'
            }}
          />
        </div>
        {isComplete && (
          <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '14px' }}></i>
        )}
      </div>
    );
  };

  const calculateSummary = (progress) => {
    if (!progress || progress.length === 0) return { totalAchieved: 0, totalTarget: 0, daysCompleted: 0 };

    return progress.reduce((acc, day) => ({
      totalAchieved: acc.totalAchieved + day.achieved_xp,
      totalTarget: acc.totalTarget + day.target_xp,
      daysCompleted: acc.daysCompleted + (day.achieved_xp >= day.target_xp && day.target_xp > 0 ? 1 : 0)
    }), { totalAchieved: 0, totalTarget: 0, daysCompleted: 0 });
  };

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} data-testid="progress-modal">
      <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <div className="d-flex align-items-center">
              <span
                className={`zodiac-icon zodiac-icon-${child.avatar} zodiac-icon-sm zodiac-icon-circle me-3`}
                role="img"
                aria-label={child.zodiac_name}
              ></span>
              <div>
                <h5 className="modal-title mb-0">{t('dashboard.index.children.progress_modal.title', { name: child.username })}</h5>
                <small className="text-muted">{t('dashboard.index.children.progress_modal.subtitle')}</small>
              </div>
            </div>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            {/* Tab Navigation */}
            <ul className="nav nav-tabs mb-4" data-testid="progress-tabs">
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === TAB_BY_DATE ? 'active' : ''}`}
                  onClick={() => setActiveTab(TAB_BY_DATE)}
                  data-testid="tab-by-date"
                >
                  <i className="bi bi-calendar3 me-2"></i>
                  {t('dashboard.index.children.progress_modal.tab_by_date')}
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === TAB_BY_ACTIVITY ? 'active' : ''}`}
                  onClick={() => setActiveTab(TAB_BY_ACTIVITY)}
                  data-testid="tab-by-activity"
                >
                  <i className="bi bi-graph-up me-2"></i>
                  {t('dashboard.index.children.progress_modal.tab_by_activity')}
                </button>
              </li>
            </ul>

            {/* Tab Content */}
            {activeTab === TAB_BY_DATE && (
              <div data-testid="tab-content-by-date">
                {isLoading && (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">{t('common.loading')}</span>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="alert alert-danger">
                    {t('common.error')}: {error.message}
                  </div>
                )}

                {data && (
                  <>
                    {/* Summary Stats */}
                    {(() => {
                      const summary = calculateSummary(data.progress);
                      return (
                        <div className="row g-3 mb-4">
                          <div className="col-4">
                            <div className="bg-light rounded-3 p-3 text-center">
                              <div className="fw-bold text-primary" style={{ fontSize: '24px' }}>
                                {formatNumber(summary.totalAchieved)}
                              </div>
                              <small className="text-muted">{t('dashboard.index.children.progress_modal.total_xp')}</small>
                            </div>
                          </div>
                          <div className="col-4">
                            <div className="bg-light rounded-3 p-3 text-center">
                              <div className="fw-bold text-success" style={{ fontSize: '24px' }}>
                                {summary.daysCompleted}
                              </div>
                              <small className="text-muted">{t('dashboard.index.children.progress_modal.days_completed')}</small>
                            </div>
                          </div>
                          <div className="col-4">
                            <div className="bg-light rounded-3 p-3 text-center">
                              <div className="fw-bold text-info" style={{ fontSize: '24px' }}>
                                {summary.totalTarget > 0 ? Math.round((summary.totalAchieved / summary.totalTarget) * 100) : 0}%
                              </div>
                              <small className="text-muted">{t('dashboard.index.children.progress_modal.overall_progress')}</small>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Daily Progress Table */}
                    <div className="table-responsive" style={{ maxHeight: '400px' }}>
                      <table className="table table-hover mb-0">
                        <thead className="table-light sticky-top">
                          <tr>
                            <th>{t('dashboard.index.children.progress_modal.date')}</th>
                            <th className="text-end">{t('dashboard.index.children.progress_modal.target')}</th>
                            <th className="text-end">{t('dashboard.index.children.progress_modal.achieved')}</th>
                            <th style={{ width: '150px' }}>{t('dashboard.index.children.progress_modal.progress')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.progress.slice().reverse().map((day) => (
                            <tr key={day.date}>
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-link p-0 text-start fw-medium text-decoration-none"
                                  onClick={() => setSelectedDate(day.date)}
                                  title={t('dashboard.index.children.progress_modal.view_details')}
                                >
                                  {formatDate(day.date, 'short')}
                                  <i className="bi bi-chevron-right ms-1 small text-muted"></i>
                                </button>
                              </td>
                              <td className="text-end text-muted">
                                {formatNumber(day.target_xp)} XP
                              </td>
                              <td className="text-end">
                                <span className={day.achieved_xp >= day.target_xp && day.target_xp > 0 ? 'text-success fw-medium' : ''}>
                                  {formatNumber(day.achieved_xp)} XP
                                </span>
                              </td>
                              <td>
                                {renderProgressBar(day.achieved_xp, day.target_xp)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === TAB_BY_ACTIVITY && (
              <div data-testid="tab-content-by-activity">
                <ActivityScoreView
                child={child}
                t={t}
                formatDate={formatDate}
              />
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {t('common.close')}
            </button>
          </div>
        </div>
      </div>

      {/* Daily Activities Modal */}
      {selectedDate && (
        <DailyActivities
          child={child}
          date={selectedDate}
          onClose={() => setSelectedDate(null)}
          t={t}
          formatNumber={formatNumber}
          formatDate={formatDate}
        />
      )}
    </div>
  );
};
